import { Link, useParams } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { Pagina } from "../../components/Pagina.js";
import { Falha, Girando, Selo, cx } from "../../components/ui.js";
import { LinhaConfig } from "./LinhaConfig.js";
import { PainelWhatsApp } from "./PainelWhatsApp.js";
import { PainelEmail } from "./PainelEmail.js";
import { SECOES, chavesDa, type ConfigDaLista, type Secao } from "./secoes.js";

/**
 * Configurações do sistema.
 *
 * Uma seção por endereço, em vez de tudo numa página só: com vinte e nove chaves,
 * a página única obrigava a rolar procurando pelo nome — e no celular, que é o
 * alvo deste sistema, era pior ainda.
 *
 * As chaves vêm do banco com sua categoria e descrição; o seed é quem declara
 * quais existem. Uma chave nova aparece aqui sozinha, sem alterar esta tela.
 */
export function ConfiguracoesPage() {
  const { secao: slug } = useParams();
  const lista = trpc.config.list.useQuery();
  const secao = SECOES.find((s) => s.slug === slug);

  if (lista.isLoading) {
    return (
      <Pagina titulo="Configurações">
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      </Pagina>
    );
  }

  // Nenhuma outra página trata erro de query. Esta precisa: sem as configurações
  // não há o que mostrar, e falhar em branco pareceria "não há nada aqui".
  if (lista.isError) {
    return (
      <Pagina titulo="Configurações">
        <Falha
          mensagem={`Não foi possível carregar as configurações. ${lista.error.message}`}
          tentarNovamente={() => void lista.refetch()}
        />
      </Pagina>
    );
  }

  const todas = lista.data ?? [];

  if (slug !== undefined && secao === undefined) {
    return (
      <Pagina titulo="Configurações">
        <Falha mensagem={`A seção "${slug}" não existe.`} />
        <div className="mt-4">
          <Link to="/admin/config" className="text-[0.9375rem] text-brasa underline">
            Ver todas as seções
          </Link>
        </div>
      </Pagina>
    );
  }

  return secao ? <DetalheSecao secao={secao} todas={todas} /> : <IndiceSecoes todas={todas} />;
}

/** O índice: cada seção com o que há dentro, para escolher onde entrar. */
function IndiceSecoes({ todas }: { todas: ConfigDaLista[] }) {
  const integracoes = trpc.config.integrationStatus.useQuery();

  return (
    <Pagina titulo="Configurações" descricao="Credenciais e ajustes do sistema.">
      <div className="grid gap-3 sm:grid-cols-2">
        {SECOES.map((s) => {
          const chaves = chavesDa(s, todas);
          if (chaves.length === 0) return null;

          const pendentes = chaves.filter((c) => c.isSecret && !c.hasValue).length;

          return (
            <Link
              key={s.slug}
              to={`/admin/config/${s.slug}`}
              className={cx(
                "flex flex-col gap-1 rounded-peca border border-borda bg-superficie-alta p-4",
                "transition-colors hover:border-borda-forte",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-display text-[1.125rem] leading-tight">{s.titulo}</span>
                {s.categoria === null ? (
                  <Selo tom="quieto">{chaves.length} sem uso</Selo>
                ) : pendentes > 0 ? (
                  <Selo tom="descartar">{pendentes} em branco</Selo>
                ) : (
                  <Selo tom="quieto">{chaves.length}</Selo>
                )}
              </div>
              <span className="text-[0.875rem] leading-relaxed text-texto-suave">{s.sobre}</span>
            </Link>
          );
        })}
      </div>

      {integracoes.data && (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="text-[0.8125rem] text-texto-fraco">Integrações:</span>
          {[
            { rotulo: "OpenRouter", ok: integracoes.data.openrouter },
            { rotulo: "fal.ai", ok: integracoes.data.fal },
            { rotulo: "Bunny", ok: integracoes.data.bunny },
            { rotulo: "WhatsApp", ok: integracoes.data.whatsapp },
          ].map((i) => (
            <Selo key={i.rotulo} tom={i.ok ? "fixado" : "quieto"}>
              {i.rotulo}
              {i.ok ? "" : " pendente"}
            </Selo>
          ))}
        </div>
      )}
    </Pagina>
  );
}

/** Uma seção: só as chaves dela, com a navegação para as demais. */
function DetalheSecao({ secao, todas }: { secao: Secao; todas: ConfigDaLista[] }) {
  const chaves = chavesDa(secao, todas);

  return (
    <Pagina titulo={secao.titulo} descricao={secao.sobre}>
      <SubNavegacao atual={secao.slug} todas={todas} />

      {/* Estado da conexão antes das credenciais: é o que se vem conferir, e as
          credenciais só interessam quando algo está errado com ele. */}
      {secao.slug === "whatsapp" && (
        <div className="mt-6">
          <PainelWhatsApp />
        </div>
      )}

      {secao.slug === "email" && (
        <div className="mt-6">
          <PainelEmail />
        </div>
      )}

      <div className="mt-6 flex flex-col gap-5">
        {chaves.map((c) => (
          <LinhaConfig key={c.key} config={c} />
        ))}
      </div>
    </Pagina>
  );
}

/**
 * Navegação entre seções.
 *
 * Rola na horizontal no celular em vez de quebrar em várias linhas: sete itens
 * empilhados empurrariam o conteúdo para fora da primeira tela.
 */
function SubNavegacao({ atual, todas }: { atual: string; todas: ConfigDaLista[] }) {
  return (
    <nav className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
      <Link
        to="/admin/config"
        className="shrink-0 rounded-peca border border-borda px-3 py-2 text-[0.875rem] text-texto-suave transition-colors hover:text-texto"
      >
        Todas
      </Link>
      {SECOES.filter((s) => chavesDa(s, todas).length > 0).map((s) => (
        <Link
          key={s.slug}
          to={`/admin/config/${s.slug}`}
          aria-current={s.slug === atual ? "page" : undefined}
          className={cx(
            "shrink-0 rounded-peca border px-3 py-2 text-[0.875rem] transition-colors",
            s.slug === atual
              ? "border-brasa bg-brasa-fraca text-brasa"
              : "border-borda text-texto-suave hover:text-texto",
          )}
        >
          {s.titulo}
        </Link>
      ))}
    </nav>
  );
}
